import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '@/lib/api'
import { canAny, CAP } from '@/lib/can'
import { queryKeys } from '@/lib/query'
import type {
  CapabilityCatalogue,
  CurrentUser,
  Role,
  RoleChange,
  RoleRecord,
} from '@/types/domain'

/* Roles and what each one may do — migration 0021.
 *
 * The WRITES live under /admin behind `admin.roles.manage`, which is a
 * NARROWER guard than the rest of user management: whoever holds it can grant
 * themselves every other capability in the product, so a founder delegating
 * day-to-day account upkeep should not be handing this over with it. The role
 * LIST is read by the account screen as well — its role picker and the user
 * card's «Права роли» (S-7) — so `GET /admin/roles` answers either
 * `admin.roles.manage` or `admin.users.manage` (owner decision O5).
 *
 * There is no mock branch in this file, deliberately, where the other settings
 * hooks have one. A mocked role list would be a mocked ACCESS MATRIX — the
 * screen would show permissions that nothing enforces, and the one thing worse
 * than an authorization screen that refuses to load is one that lies about what
 * is granted. With mocks on, these queries fail and the screen says so.
 */

const roleKeys = {
  all: ['settings', 'roles'] as const,
  capabilities: ['settings', 'capabilities'] as const,
}

/** Whether this session may read the role list: the role editor's holder or
 *  the account manager's (S-7, O5). The server re-checks on the request. */
export function mayReadRoles(user: CurrentUser | null | undefined): boolean {
  return canAny(user, CAP.adminRolesManage, CAP.adminUsersManage)
}

/** The role list. `enabled: false` sends nothing — the account screen passes
 *  `mayReadRoles(session)`, so a reader the server would refuse makes no
 *  request that can only come back 403. */
export function useRoles({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<RoleRecord[]>({
    queryKey: roleKeys.all,
    queryFn: async () => (await api.get<{ items: RoleRecord[] }>('/admin/roles')).items,
    enabled,
  })
}

/** The vocabulary the role editor renders: every capability the SERVER
 *  registers, plus the surfaces and levels the scope editor offers.
 *
 *  Fetched rather than hard-coded because the server's registry is what
 *  actually gets checked — a capability this client invented would be a
 *  permission that does nothing, rendered as though it did something.
 *
 *  Effectively immutable for the life of a deployment, so it is cached hard:
 *  it changes when the server is redeployed, and a refetch on window focus
 *  would be a request per tab switch for a list that cannot have moved. */
export function useCapabilityCatalogue() {
  return useQuery<CapabilityCatalogue>({
    queryKey: roleKeys.capabilities,
    queryFn: async () => await api.get<CapabilityCatalogue>('/admin/capabilities'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export function useCreateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (change: RoleChange) => await api.post<RoleRecord>('/admin/roles', change),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleKeys.all })
      void qc.invalidateQueries({ queryKey: queryKeys.roleNames() })
    },
  })
}

export function useAmendRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ key, change }: { key: Role; change: RoleChange }) =>
      await api.put<RoleRecord>(`/admin/roles/${encodeURIComponent(key)}`, change),
    onSuccess: () => {
      // Both lists: amending a role changes what its holders can do, so the
      // account list (and an open user card's «Права роли») is stale too.
      void qc.invalidateQueries({ queryKey: roleKeys.all })
      // The name every team card prints (GET /api/roles) — a rename is the
      // reason most amends happen.
      void qc.invalidateQueries({ queryKey: queryKeys.roleNames() })
      void qc.invalidateQueries({ queryKey: queryKeys.settings.users() })
      // And the CALLER's own session: a founder who just edited their own
      // role's capabilities is looking at a navigation rendered from the old
      // set. Re-reading /session is what makes the change visible without a
      // reload — the server has already applied it.
      void qc.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (key: Role) =>
      await api.delete<void>(`/admin/roles/${encodeURIComponent(key)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleKeys.all })
      void qc.invalidateQueries({ queryKey: queryKeys.roleNames() })
    },
  })
}

/* The role writes' 409 refusals, recognised by the token the server puts in
 * the problem detail (identity/roles_handlers.go `fail`, the http-api.md
 * Conflict contract). Predicates here, beside the hooks that meet them, so each
 * wire token is spelled in exactly one place on this side; the screen words
 * them through `roleRefusalMessage` and never shows the token. */

function conflictToken(error: unknown, token: string): boolean {
  return error instanceof ApiError && error.code === 'conflict' && error.problem.detail === token
}

/** The role still has holders, so it cannot be deleted. */
export function roleInUse(error: unknown): boolean {
  return conflictToken(error, 'role-in-use')
}

/** A seeded (built-in) role cannot be deleted. */
export function roleIsSystem(error: unknown): boolean {
  return conflictToken(error, 'system-role')
}

/** A seeded role's key cannot be changed — worded apart from deleting one. */
export function roleKeyFixed(error: unknown): boolean {
  return conflictToken(error, 'system-role-key-fixed')
}

/** The superuser role cannot be deleted, nor its capabilities changed. */
export function roleIsSuperuser(error: unknown): boolean {
  return conflictToken(error, 'superuser-role')
}

/** The change would leave no active super-admin. */
export function roleLastSuperAdmin(error: unknown): boolean {
  return conflictToken(error, 'last-super-admin')
}
